import { Modal } from "react-bootstrap";
import React, { useCallback } from "react";
import Button from "../Button/Button";
import styles from "./BaseModal.module.scss";

interface ModalProps {
  show: boolean;
  title: string;
  body: string | React.ReactElement;
  onYes: () => void;
  onNo: () => void;
  // Backdrop-click / header-X dismissal; defaults to onNo.
  onDismiss?: () => void;
  yesLabel: string;
  noLabel: string;
  yesTestId?: string;
  modalTestId?: string;
  placeholder?: string | undefined;
  yesDisabled?: boolean;
  yesVariant?: "primary" | "danger";
  // Defaults to false (non-dismissible), matching current behavior.
  closeOnEscape?: boolean;
  // Blocks Enter/Space on the Yes button so only a mouse click confirms.
  mouseOnlyConfirm?: boolean;
}

const blockConfirmKey = (e: KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
  }
};

export const BaseModal = ({
  show,
  title,
  body,
  onYes,
  onNo,
  onDismiss,
  yesLabel = "",
  noLabel = "",
  yesTestId,
  modalTestId,
  yesDisabled = false,
  yesVariant = "primary",
  closeOnEscape = false,
  mouseOnlyConfirm = false,
}: ModalProps) => {
  const handleDismiss = onDismiss ?? onNo;
  const yesRef = useCallback(
    (el: HTMLElement | null) => {
      if (el && mouseOnlyConfirm) {
        el.addEventListener("keydown", blockConfirmKey);
        el.addEventListener("keyup", blockConfirmKey);
      }
    },
    [mouseOnlyConfirm],
  );

  return (
    <Modal
      show={show}
      contentClassName={`${styles["confirmation-dialog"]}`}
      backdrop={closeOnEscape ? true : "static"}
      centered={true}
      keyboard={closeOnEscape}
      onHide={handleDismiss}
      onEscapeKeyDown={
        closeOnEscape
          ? (e: KeyboardEvent) => {
              // Avoid onHide also firing for the same Escape press.
              e.preventDefault();
              onNo();
            }
          : undefined
      }
      data-testid={modalTestId}
    >
      <Modal.Header className={`${styles["modal-header"]}`} closeButton={true}>
        <span className="ms-auto fw-bold">{title}</span>
      </Modal.Header>
      <Modal.Body>
        {typeof body === "string" ? <p>{body}</p> : body}
        <div className={`${styles["confirmation-buttons"]} m-button-group`}>
          <Button text={noLabel} type="secondary" onClick={onNo} />
          <Button
            ref={yesRef}
            text={yesLabel}
            type={yesVariant}
            onClick={onYes}
            disabled={yesDisabled}
            testId={yesTestId}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};
